export const extractImageData = (files: any[]): { url: string; info: string }[] => {
  return files.flatMap((fileGroup) => {
    if (!Array.isArray(fileGroup)) return [];
    return fileGroup
      .filter((file: { imgPath?: string }) => !!file.imgPath)
      .map((file: any) => {
        const isHeicOrHeif =
          file.imgPath?.includes(".heic") ||
          file.imgPath?.includes(".HEIC") ||
          file.imgPath?.includes(".heif") ||
          file.imgPath?.includes(".HEIF");
        const url = isHeicOrHeif ? file.thumbnailPath || "" : file.imgPath;
        const info = isHeicOrHeif ? "image-stepper-file-format-not-supported" : "";
        return { url, info };
      });
  });
};

export function formatBytes(bytes: number): string {
  const mb = bytes / (1024 * 1024);
  if (mb >= 1) return `${mb.toFixed(1)} MB`;
  const kb = bytes / 1024;
  return `${Math.round(kb)} KB`;
}

export async function getImageDimensions(
  file: File,
  text: string,
): Promise<{ width: number; height: number } | null> {
  // SVG (tricky) → skip dimensions-check (return null)
  if (file.type === "image/svg+xml") return null;

  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error(text));
      img.src = url;
    });
    return { width: img.naturalWidth, height: img.naturalHeight };
  } finally {
    URL.revokeObjectURL(url);
  }
}

export function isSquareRatio(width: number, height: number, tolerance = 0.12): boolean {
  // tolerance=0.12 ~ allowed 0.88-1.12
  if (height === 0) return false;
  const ratio = width / height;
  return Math.abs(ratio - 1) <= tolerance;
}

export function isWideRatio(width: number, height: number, minRatio = 2.2): boolean {
  if (height === 0) return false;
  return width / height >= minRatio;
}

export async function urlToDataUrl(url: string): Promise<string> {
  const res = await fetch(url, { cache: "no-store" });
  if (!res.ok) throw new Error(`Failed to fetch image: ${res.status}`);
  const blob = await res.blob();

  // If the image is an SVG, we must rasterize it to PNG for @react-pdf/renderer
  if (blob.type === "image/svg+xml") {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const svgUrl = URL.createObjectURL(blob);
      img.onload = () => {
        const canvas = document.createElement("canvas");
        // Use natural dimensions if available, otherwise default to a reasonable size
        // We multiply by a factor (e.g., 2) for better quality in the PDF
        const scale = 2;
        canvas.width = (img.naturalWidth || 500) * scale;
        canvas.height = (img.naturalHeight || 500) * scale;
        const ctx = canvas.getContext("2d");
        if (ctx) {
          ctx.fillStyle = "transparent";
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          const pngDataUrl = canvas.toDataURL("image/png");
          URL.revokeObjectURL(svgUrl);
          resolve(pngDataUrl);
        } else {
          reject(new Error("Failed to get canvas context"));
        }
      };
      img.onerror = (err) => {
        URL.revokeObjectURL(svgUrl);
        reject(new Error("Failed to load SVG for rasterization"));
      };
      img.src = svgUrl;
    });
  }
  return await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("FileReader failed"));
    reader.onload = () => resolve(reader.result as string);
    reader.readAsDataURL(blob);
  });
}

// helper: await an <img> load
export async function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}
